import { memo, useCallback, useContext, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "app/components/ui/tabs/Tabs";
import {
	MERCH_CIRCUIT_CODE,
	RESOLUTION,
	WEBSITE_TUNNEL_LABELS,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import "./WebsiteTunnelSwitcher.scss";
import classNames from "classnames";
import { Link } from "react-router-dom";
import AppGlobalsContext from "app/AppGlobalsContext";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import IconHolidays from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconHolidays";
import IconStopover from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconStopover";
import IconHotelOnly from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconHotelOnly";
import IconCircuit from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconCircuit";
import IconUmrah from "app/pages/.shared/static/icons/WebsiteTunnelTypes/IconUmrah";
import {
	AsyncHotelOnlySearchFormContainer,
	AsyncSmartDPSearchFormContainer,
	AsyncStopoverSearchFormContainer,
} from "app/routes";

const WEBSITE_TUNNEL_ICONS = {
	[WEBSITE_TUNNEL_TYPES.HOLIDAYS]: IconHolidays,
	[WEBSITE_TUNNEL_TYPES.STOPOVER]: IconStopover,
	[WEBSITE_TUNNEL_TYPES.HOTEL_ONLY]: IconHotelOnly,
	[WEBSITE_TUNNEL_TYPES.MERCH_CIRCUIT]: IconCircuit,
	[WEBSITE_TUNNEL_TYPES.UMRAH]: IconUmrah,
};

const getTunnelLabelId = (tunnelType, hasSingleStopoverCity) => {
	if (tunnelType === WEBSITE_TUNNEL_TYPES.STOPOVER && hasSingleStopoverCity) {
		return "root.tab.label.stopover.at.city";
	}

	return WEBSITE_TUNNEL_LABELS[tunnelType];
};

// click on those tabs will not highlight them because they will open a new browser tab
const TUNNEL_TYPES_WITH_EXTERNAL_REDIRECT = [
	WEBSITE_TUNNEL_TYPES.MERCH_CIRCUIT,
	WEBSITE_TUNNEL_TYPES.UMRAH,
];

const WebsiteTunnelSwitcher = ({
	websiteTunnels = [],
	stopoverCities,
	disableFormPersistance = true,
}) => {
	const { resolution } = useContext(AppGlobalsContext);
	const tabRefs = useRef([]);

	const { track } = useAnalytics();

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const hasMultipleTunnels = websiteTunnels?.length > 1;
	const [selectedIndex, setSelectedIndex] = useState(0);
	const doubleTab = websiteTunnels?.length === 2;
	const hasSingleStopoverCity = stopoverCities?.length === 1;

	const switcherClass = classNames("website-tunnel-switcher", {
		"website-tunnel-switcher--multiple": hasMultipleTunnels,
		"website-tunnel-switcher--double-tabs": doubleTab,
	});

	const switcherRef = useRef(null);
	const [isAutoScrollActive, setIsAutoScrollActive] = useState(false);

	const handleFieldFocus = useCallback(() => {
		const fieldElement = switcherRef?.current;
		if (fieldElement) {
			setIsAutoScrollActive(true);
			const { top } = fieldElement.getBoundingClientRect();
			const currentScroll = window.scrollY;

			const offsetTop = currentScroll + top - 96;

			window.scrollTo({
				top: offsetTop,
				behavior: "smooth",
			});

			setTimeout(() => {
				setIsAutoScrollActive(false);
			}, 300);
		}
	}, [switcherRef?.current]);

	const handleTabSelect = index => {
		const selectedTunnel = websiteTunnels[index];

		track("click_website_tunnel_tab", {
			type: selectedTunnel?.websiteTunnelType,
		});

		if (!TUNNEL_TYPES_WITH_EXTERNAL_REDIRECT.includes(selectedTunnel?.websiteTunnelType)) {
			setSelectedIndex(index);
		}
	};

	return (
		<div className={switcherClass} ref={switcherRef}>
			<Tabs value={selectedIndex} onValueChange={handleTabSelect}>
				{hasMultipleTunnels && (
					<TabsList className="website-tunnel-switcher__tabs-list" fullWidth={isMobile}>
						{websiteTunnels?.map((tunnel = {}, index) => {
							const websiteTunnelLabelId = getTunnelLabelId(
								tunnel.websiteTunnelType,
								hasSingleStopoverCity
							);
							const isActive = index === selectedIndex;
							const WebsiteTunnelIcon =
								WEBSITE_TUNNEL_ICONS[tunnel?.websiteTunnelType];

							const tabContent = (
								<>
									{WebsiteTunnelIcon && (
										<WebsiteTunnelIcon
											className={classNames("website-tunnel-switcher__icon", {
												"website-tunnel-switcher__icon--active": isActive,
											})}
										/>
									)}
									<Typography
										variant={TYPOGRAPHY_VARIANTS.REGULAR}
										isBold
										className="website-tunnel-switcher__label"
									>
										<FormattedMessage
											id={websiteTunnelLabelId}
											values={
												hasSingleStopoverCity
													? { city: stopoverCities[0].label }
													: undefined
											}
										/>
									</Typography>
								</>
							);

							const tabsTriggerProps = {
								value: index,
								colorTextWhenActive: true,
								activeColor: "var(--color-active, $active-color)",
							};

							if (tunnel.websiteTunnelType === WEBSITE_TUNNEL_TYPES.MERCH_CIRCUIT) {
								return (
									<TabsTrigger asChild key={index} {...tabsTriggerProps}>
										<Link
											to={`/merch?code=${MERCH_CIRCUIT_CODE}`}
											target={isMobile ? "_self" : "_blank"}
											rel="noopener noreferrer"
											className="website-tunnel-switcher__merch-circuit"
										>
											{tabContent}
										</Link>
									</TabsTrigger>
								);
							}

							if (tunnel.websiteTunnelType === WEBSITE_TUNNEL_TYPES.UMRAH) {
								return (
									<TabsTrigger asChild key={index} {...tabsTriggerProps}>
										<Link
											to={`/merch?code=UMRAH`}
											target={isMobile ? "_self" : "_blank"}
											rel="noopener noreferrer"
											className="website-tunnel-switcher__merch-umrah"
										>
											{tabContent}
										</Link>
									</TabsTrigger>
								);
							}

							return (
								<TabsTrigger key={index} {...tabsTriggerProps}>
									<div
										className={classNames("website-tunnel-switcher__tab", {
											"website-tunnel-switcher__tab--selected": isActive,
										})}
										ref={el => (tabRefs.current[index] = el)}
									>
										{tabContent}
									</div>
								</TabsTrigger>
							);
						})}
					</TabsList>
				)}

				{websiteTunnels?.map((tunnel = {}, index) => (
					<TabsContent
						key={index}
						value={index}
						className={classNames("website-tunnel-switcher__content", {
							"website-tunnel-switcher__content--multiple-tunnels":
								hasMultipleTunnels,
						})}
					>
						{tunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.STOPOVER && (
							<AsyncStopoverSearchFormContainer
								disableFormPersistance={disableFormPersistance}
								onFieldFocus={handleFieldFocus}
								stopoverCities={stopoverCities}
								isAutoScrollActive={isAutoScrollActive}
							/>
						)}
						{tunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.HOLIDAYS && (
							<AsyncSmartDPSearchFormContainer
								disableFormPersistance={disableFormPersistance}
								onFieldFocus={handleFieldFocus}
								isAutoScrollActive={isAutoScrollActive}
							/>
						)}
						{tunnel?.websiteTunnelType === WEBSITE_TUNNEL_TYPES.HOTEL_ONLY && (
							<AsyncHotelOnlySearchFormContainer
								disableFormPersistance={disableFormPersistance}
								onFieldFocus={handleFieldFocus}
								isAutoScrollActive={isAutoScrollActive}
							/>
						)}
					</TabsContent>
				))}
			</Tabs>
		</div>
	);
};

WebsiteTunnelSwitcher.propTypes = {
	websiteTunnels: PropTypes.array,
	stopoverCities: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			code: PropTypes.number,
		})
	),
	disableFormPersistance: PropTypes.bool,
};

export default memo(WebsiteTunnelSwitcher);
